/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
// v77.43 — Full Activity Log page.
//
// Single canonical view that follows the same child-scope rules as the
// rest of the app:
//   • All children → combined chronological feed across every child
//     the current user has access to.
//   • Specific child → filtered to that child only.
// Account-level entries (no child id) are shown in both views and
// labelled "Account" with neutral styling.
//
// Reuses GET /api/audit which is already permission-safe (v77.16) and
// is the same data source the Court Bundle export uses, so a
// timestamp seen here matches the exported PDF exactly.

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { Clock, Filter, Loader2 } from "lucide-react";

const formatTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) { return iso; }
};

// Friendly verbs for raw audit `action` codes. Falls back to the raw
// code (kebab-cased) so unknown actions still render readably.
const ACTION_LABELS = {
  create: "Created",
  update: "Updated",
  delete: "Removed",
  send: "Sent",
  read: "Read",
  flag: "Flagged",
  unflag: "Unflagged",
  upload: "Uploaded",
  pay: "Paid",
  settle: "Settled",
  approve: "Approved",
  decline: "Declined",
  invite: "Invited",
  revoke: "Revoked access",
  register: "Joined",
  login: "Signed in",
  logout: "Signed out",
  export: "Exported",
};
const ENTITY_LABELS = {
  message: "message",
  event: "event",
  expense: "expense",
  journal_entry: "journal entry",
  document: "document",
  request: "request",
  custody_schedule: "parenting schedule",
  custody_override: "schedule override",
  child: "child profile",
  user: "account",
  household: "household",
  invite: "invite",
  court_bundle: "court bundle",
  bug_report: "bug report",
};

const describe = (row) => {
  const verb = ACTION_LABELS[row.action] || (row.action || "Did something").replace(/_/g, " ");
  const entity = ENTITY_LABELS[row.entity] || (row.entity || "");
  return entity ? `${verb} ${entity}` : verb;
};

export default function ActivityPage() {
  const { user, members } = useAuth();
  const { selectedChildId, children: scopeChildren } = useChildScope();

  const [rows, setRows] = useState(null); // null = loading
  const [actionFilter, setActionFilter] = useState("all");

  useEffect(() => {
    setRows(null);
    const url = selectedChildId
      ? `/audit?limit=500&child_id=${encodeURIComponent(selectedChildId)}`
      : `/audit?limit=500`;
    api.get(url)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRows([]));
  }, [selectedChildId]);

  // Build an actor-name lookup from the household members already cached
  // by AuthContext. Falls back to "—" for unknown actors.
  const actorName = useMemo(() => {
    const map = {};
    (members || []).forEach((m) => { if (m?.id) map[m.id] = m.name || m.email || "—"; });
    if (user?.id) map[user.id] = user.name || user.email || map[user.id] || "—";
    return (id) => (id && map[id]) || "—";
  }, [members, user]);

  const distinctActions = useMemo(() => {
    if (!rows) return [];
    const set = new Set();
    rows.forEach((r) => { if (r.action) set.add(r.action); });
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    if (actionFilter === "all") return rows;
    return rows.filter((r) => r.action === actionFilter);
  }, [rows, actionFilter]);

  const childCountAccessible = (scopeChildren || []).filter(
    (c) => (c.status || "active") !== "archived",
  ).length;

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div>
        <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A] dark:text-[#7A8A91]">
          Records
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1 text-[#2E3A3F] dark:text-[#E8EEF0]">
          Activity log
        </h1>
        {!selectedChildId ? (
          <p
            className="text-sm mt-2 text-[#6F828A] dark:text-[#A8B6BB]"
            title="You only see activity for children you are linked to."
            data-testid="activity-helper-all-children"
          >
            Showing activity for {childCountAccessible === 1 ? "the child" : "children"} you have access to. Account-level events appear here too.
          </p>
        ) : (
          <p
            className="text-sm mt-2 text-[#6F828A] dark:text-[#A8B6BB]"
            data-testid="activity-helper-single-child"
          >
            Filtered to a single child. Switch to &ldquo;All children&rdquo; to see the full timeline.
          </p>
        )}
      </div>

      {/* Action filter — single, simple control. The structure leaves
          room for future per-date / per-actor filters without a redesign. */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-[#6F828A] dark:text-[#7A8A91]" strokeWidth={1.75} />
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="text-sm rounded-sm border border-[#D9E1E3] dark:border-[#2A3539] bg-white dark:bg-[#1A2225] text-[#2E3A3F] dark:text-[#E8EEF0] h-9 px-2"
          data-testid="activity-action-filter"
        >
          <option value="all">All actions</option>
          {distinctActions.map((a) => (
            <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
          ))}
        </select>
      </div>

      {/* The list itself. Single chronological feed, most recent first.
          Designed to scroll within the page rather than the section so
          the heading + helper line stay anchored on tall screens. */}
      <div
        className="border border-[#D9E1E3] dark:border-[#2A3539] bg-white dark:bg-[#1A2225] rounded-md"
        data-testid="activity-list-container"
      >
        {rows === null ? (
          <div className="px-4 py-10 flex items-center justify-center text-sm text-[#6F828A] dark:text-[#A8B6BB]">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-[#6F828A] dark:text-[#A8B6BB]" data-testid="activity-empty">
            <Clock className="w-5 h-5 mx-auto mb-2" strokeWidth={1.5} />
            {actionFilter === "all"
              ? "No activity yet."
              : "No activity matching this filter."}
          </div>
        ) : (
          <ul className="divide-y divide-[#EEF3F4] dark:divide-[#1F2A2D] max-h-[70vh] overflow-y-auto">
            {filtered.map((r) => (
              <li
                key={r.id}
                className="px-3 py-2.5 flex items-center justify-between gap-3 text-sm"
                data-testid={`activity-row-${r.id}`}
              >
                <div className="min-w-0 flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center gap-1.5 text-xs px-1.5 py-0.5 rounded-sm border"
                    style={{
                      backgroundColor: r.child_color
                        ? `${r.child_color}1A` // 10% alpha
                        : undefined,
                      borderColor: r.child_color || "#D9E1E3",
                      color: r.child_color === "#6F828A" ? undefined : (r.child_color || undefined),
                    }}
                    title={r.child_name}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: r.child_color || "#6F828A" }}
                      aria-hidden="true"
                    />
                    {r.child_name || "Account"}
                  </span>
                  <span className="text-[#2E3A3F] dark:text-[#E8EEF0]">
                    {describe(r)}
                  </span>
                  <span className="text-[#6F828A] dark:text-[#A8B6BB]">
                    · {actorName(r.actor_id)}
                  </span>
                </div>
                <div className="text-xs text-[#6F828A] dark:text-[#A8B6BB] font-mono-data flex-shrink-0">
                  {formatTime(r.created_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
